import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { API, apiError, formatINR, formatDate } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// HTML invoice for direct browser printing. Opened in new window with ?auto=1 to auto-print.
export default function InvoicePrint() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "branch_head";
  const nav = useNavigate();
  const [sale, setSale] = useState(null);
  const [store, setStore] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    Promise.all([API.get(`/sales/${id}`), API.get(`/sales/${id}/store`)]).then(([s, st]) => {
      setSale(s.data); setStore(st.data);
    });
  }, [id]);

  useEffect(() => {
    if (sale && store && params.get("auto") === "1") {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [sale, store, params]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete invoice ${sale.bill_number}? Stock will be restored and any linked customer ledger entries removed. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await API.delete(`/sales/${id}`);
      toast.success(`Invoice ${sale.bill_number} deleted`);
      // close the window if opened via popup, otherwise navigate
      if (window.opener && !window.opener.closed) {
        try { window.opener.location.reload(); } catch { /* cross-origin */ }
        window.close();
      } else {
        nav("/sales/history");
      }
    } catch (e) { toast.error(apiError(e)); setDeleting(false); }
  };

  if (!sale || !store) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-100 p-6 print:bg-white print:p-0" data-testid="invoice-print">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          .noprint { display: none !important; }
          body { background: white !important; }
        }
        .receipt { background: white; max-width: 760px; margin: 0 auto; padding: 32px; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,.08); font-family: 'IBM Plex Sans', Arial, sans-serif; color: #0F172A; }
        @media print { .receipt { box-shadow: none; border-radius: 0; padding: 0; max-width: none; } }
        .r-header { display: flex; justify-content: space-between; align-items: start; border-bottom: 2px solid #002FA7; padding-bottom: 16px; margin-bottom: 20px; }
        .r-title { font-family: 'Manrope', sans-serif; font-size: 28px; font-weight: 800; color: #002FA7; letter-spacing: -0.02em; }
        .r-meta { font-size: 12px; color: #475569; line-height: 1.5; }
        .r-meta b { color: #0F172A; }
        .r-badge { display: inline-block; padding: 4px 10px; background: #EFF6FF; color: #1D4ED8; font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; border-radius: 4px; }
        .r-info { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px 24px; margin-bottom: 18px; font-size: 12px; }
        .r-info div { }
        .r-info label { display: block; font-size: 10px; letter-spacing: .15em; text-transform: uppercase; color: #64748B; font-weight: 700; margin-bottom: 2px; }
        .r-info span { font-weight: 600; font-size: 13px; }
        table.items { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 12.5px; }
        table.items thead th { background: #002FA7; color: white; text-align: left; padding: 8px 10px; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; }
        table.items tbody td { padding: 8px 10px; border-bottom: 1px solid #E2E8F0; }
        table.items tbody tr:nth-child(even) td { background: #F8FAFC; }
        .text-right { text-align: right; }
        .totals { margin-left: auto; width: 320px; font-size: 13px; }
        .totals .row { display: flex; justify-content: space-between; padding: 6px 12px; }
        .totals .grand { background: #002FA7; color: white; font-weight: 700; font-size: 16px; padding: 10px 12px; border-radius: 6px; margin-top: 6px; }
        .footer-note { margin-top: 28px; font-size: 11px; color: #475569; text-align: center; border-top: 1px dashed #CBD5E1; padding-top: 14px; }
      `}</style>

      <div className="noprint max-w-[760px] mx-auto mb-4 flex gap-2 justify-end">
        <button onClick={() => window.print()} className="btn-primary" data-testid="print-now-btn">🖨️ Print Invoice</button>
        <button onClick={() => window.close()} className="btn-ghost">Close</button>
      </div>

      <div className="receipt">
        <div className="r-header">
          <div>
            <div className="r-title">{store.name}</div>
            <div className="r-meta" style={{ marginTop: 6 }}>
              {store.address}<br />
              {store.gst && <>GST: <b>{store.gst}</b> &nbsp;·&nbsp; </>}
              {store.contact && <>Contact: <b>{store.contact}</b> &nbsp;·&nbsp; </>}
              {store.email}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="r-badge">Tax Invoice</div>
            <div style={{ marginTop: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700 }}>{sale.bill_number}</div>
            <div className="r-meta" style={{ marginTop: 4 }}>{new Date(sale.created_at).toLocaleString("en-IN")}</div>
          </div>
        </div>

        <div className="r-info">
          <div><label>Customer</label><span>{sale.customer_name || "Walk-in"}</span></div>
          <div><label>Phone</label><span>{sale.customer_phone || "—"}</span></div>
          <div><label>Cashier</label><span>{sale.cashier_name}</span></div>
          <div><label>Branch</label><span>{sale.branch_name || "—"}</span></div>
        </div>

        <table className="items">
          <thead>
            <tr>
              <th style={{ width: 32 }}>#</th>
              <th>Medicine</th>
              <th>Batch</th>
              <th>Expiry</th>
              <th className="text-right">MRP</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {sale.items.map((it, i) => (
              <tr key={`${it.medicine_id}-${it.batch_number || i}`}>
                <td>{i + 1}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{it.medicine_name}</div>
                  <div style={{ color: "#64748B", fontSize: 11 }}>{it.company}</div>
                </td>
                <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{it.batch_number}</td>
                <td style={{ fontSize: 11 }}>{formatDate(it.expiry_date)}</td>
                <td className="text-right">{formatINR(it.mrp)}</td>
                <td className="text-right">{it.quantity}</td>
                <td className="text-right" style={{ fontWeight: 600 }}>{formatINR(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="totals">
          <div className="row"><span>Subtotal</span><span>{formatINR(sale.subtotal)}</span></div>
          <div className="row"><span>Discount</span><span>− {formatINR(sale.discount)}</span></div>
          <div className="row"><span>GST ({sale.gst_rate}%)</span><span>{formatINR(sale.gst_amount)}</span></div>
          <div className="grand row"><span>Grand Total</span><span>{formatINR(sale.total)}</span></div>
          <div className="row" style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
            <span>Payment</span><span>{sale.payment_mode}</span>
          </div>
        </div>

        <div className="footer-note">
          Thank you for your purchase. Get well soon!<br />
          <span style={{ fontSize: 10 }}>This is a computer-generated invoice. Goods once sold are non-returnable beyond 24 hours without original packaging.</span>
        </div>
      </div>
    </div>
  );
}
